"use client";
import {
  Copy,
  Fingerprint,
  ListFilter,
  Search,
  TextQuote,
} from "lucide-react";
import {
  useState,
} from "react";
import {
  type ScanReport,
} from "../../../lib/types";
import {
  IconButton,
} from "../../shared/primitives";


export function StringsView({
  report,
  onCopy,
}: {
  report: ScanReport;
  onCopy: (value: string, message?: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string>("all");
  const [suspiciousOnly, setSuspiciousOnly] = useState(true);
  const categories = Array.from(
    new Set(report.strings.map((item) => item.category)),
  );
  const exactRecovered = report.recoveredExpressions.filter(
    (item) => item.confidence === "high",
  ).length;
  const rewriteReady = report.recoveredExpressions.filter(
    (item) => item.confidence === "high" && item.patchable,
  ).length;
  const plaintextLiterals = report.classes.reduce(
    (total, classInfo) =>
      total +
      classInfo.methods.reduce(
        (methodTotal, method) =>
          methodTotal +
          method.instructions.filter(
            (instruction) => typeof instruction.literal === "string",
          ).length,
        0,
      ),
    0,
  );
  const strings = report.strings.filter(
    (item) =>
      (!suspiciousOnly || item.suspicious) &&
      (category === "all" || item.category === category) &&
      (!query ||
        `${item.value} ${item.entry} ${item.transform ?? ""} ${item.method ?? ""} ${item.flags
          .map((flag) => `${flag.id} ${flag.label} ${flag.term}`)
          .join(" ")}`
          .toLowerCase()
          .includes(query.toLowerCase())),
  );

  return (
    <section className="panel table-panel string-panel">
      <div className="table-toolbar">
        <div className="search-field">
          <Search size={15} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search extracted strings"
            aria-label="Search strings"
          />
        </div>
        <label className="select-control">
          <ListFilter size={14} />
          <select
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            aria-label="String category"
          >
            <option value="all">All categories</option>
            {categories.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </label>
        <label className="check-control">
          <input
            type="checkbox"
            checked={suspiciousOnly}
            onChange={(event) => setSuspiciousOnly(event.target.checked)}
          />
          <span />
          Suspicious only
        </label>
      </div>
      <div className="string-coverage-strip">
        <span>
          <strong>{plaintextLiterals.toLocaleString()}</strong>
          plaintext literals
        </span>
        <span>
          <strong>{exactRecovered.toLocaleString()}</strong>
          exact recovered values
        </span>
        <span>
          <strong>{rewriteReady.toLocaleString()}</strong>
          rewrite-ready call sites
        </span>
        <span>
          <strong>
            {Math.max(0, exactRecovered - rewriteReady).toLocaleString()}
          </strong>
          analysis-only values
        </span>
      </div>
      {report.suspiciousTerms.length > 0 && (
        <div className="dictionary-strip">
          <span>
            <Fingerprint size={13} />
            Suspicious dictionary
          </span>
          <div>
            {report.suspiciousTerms.slice(0, 18).map((term) => (
              <button
                type="button"
                key={term.id}
                className={`dictionary-chip risk-${term.risk}`}
                onClick={() => {
                  setQuery(term.label);
                  setSuspiciousOnly(true);
                }}
                title={`${term.reason} ${term.count} hit${term.count === 1 ? "" : "s"} across ${term.entries} source${term.entries === 1 ? "" : "s"}.`}
              >
                {term.label}
                <small>{term.count}</small>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="string-count">
        Showing {strings.length.toLocaleString()} of{" "}
        {report.strings.length.toLocaleString()} indexed strings ·{" "}
        {report.strings
          .filter((item) => item.flags.length > 0)
          .length.toLocaleString()}{" "}
        dictionary flagged
      </div>
      <div className="data-table string-table">
        <div className="data-table-head">
          <span>Class</span>
          <span>Category</span>
          <span>Recovery</span>
          <span>Flags</span>
          <span>Value</span>
          <span />
        </div>
        {strings.slice(0, 1000).map((item) => (
          <div className="data-table-row" key={item.id}>
            <span className="path-line" title={item.entry}>
              {item.entry}
            </span>
            <span className={`string-type ${item.suspicious ? "is-suspicious" : ""}`}>
              {item.category}
            </span>
            <span
              className={`string-transform ${item.transform ? "is-recovered" : ""}`}
              title={
                item.method
                  ? `${item.transform ?? "constant"} in ${item.method}`
                  : item.transform ?? "Constant-pool string"
              }
            >
              {item.transform ?? "constant"}
            </span>
            <span className="string-flags">
              {item.flags.slice(0, 3).map((flag) => (
                <span
                  key={flag.id}
                  className={`dictionary-chip risk-${flag.risk}`}
                  title={flag.reason}
                >
                  {flag.label}
                </span>
              ))}
              {item.flags.length > 3 && (
                <small title={item.flags.slice(3).map((flag) => flag.label).join(", ")}>
                  +{item.flags.length - 3}
                </small>
              )}
            </span>
            <code title={item.value}>{item.value}</code>
            <IconButton
              label="Copy string"
              onClick={() => onCopy(item.value, "String copied")}
            >
              <Copy size={13} />
            </IconButton>
          </div>
        ))}
      </div>
      {!strings.length && (
        <div className="empty-panel">
          <TextQuote size={25} />
          <strong>No matching strings</strong>
          <span>Change the category or include contextual values.</span>
        </div>
      )}
    </section>
  );
}
